import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Spinner, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { Upload, Search, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { showSuccess, showError, showConfirm } from '../../../utils/notifications';
import { Photo, Reservation, House } from '../../../services/types';
import photosService from '../../../services/photosService';
import reservationsService from '../../../services/reservationsService';
import housesService from '../../../services/housesService';
import PhotoUploadModal from '../../../components/photos/PhotoUploadModal';
import ReservationPhotoCard from '../../../components/photos/ReservationPhotoCard';
import ReservationPhotoCarousel from '../../../components/photos/ReservationPhotoCarousel';
import { useUserData } from '../../../hooks';

const Photos = () => {
    const { canEdit, canDelete, role } = useUserData();
    const [photos, setPhotos] = useState<Photo[]>([]);
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [houses, setHouses] = useState<House[]>([]);
    const [loading, setLoading] = useState(true);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [houseFilter, setHouseFilter] = useState<string>('all');

    // Carousel viewer
    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
    const [showCarousel, setShowCarousel] = useState(false);
    const [preSelectedReservationId, setPreSelectedReservationId] = useState<string | undefined>(undefined);

    // Check if user is super admin
    const isSuperAdmin = role === 'Super Admin';

    // Detect mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Monitor state changes
    useEffect(() => {
        console.log('Photos component - State changed!');
        console.log('Photos component - reservations:', reservations);
        console.log('Photos component - reservations.length:', reservations.length);
        console.log('Photos component - houses:', houses);
        console.log('Photos component - houses.length:', houses.length);
    }, [reservations, houses]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);

        // Fetch each independently so one failure doesn't block others
        let photosData: Photo[] = [];
        let reservationsData: Reservation[] = [];
        let housesData: House[] = [];

        try {
            photosData = await photosService.getAll();
        } catch (err) {
            console.error('Error fetching photos:', err);
        }

        try {
            reservationsData = await reservationsService.getForPhotos();
        } catch (err) {
            console.error('Error fetching reservations:', err);
        }

        try {
            housesData = await housesService.getAll();
        } catch (err) {
            console.error('Error fetching houses:', err);
        }

        setPhotos(photosData);
        setReservations(reservationsData);
        setHouses(housesData);
        setLoading(false);
    };

    const handleUpload = async (data: {
        reservation_id: string;
        type: 'check-in' | 'check-out';
        files: File[];
    }) => {
        try {
            // Upload each file
            const uploadPromises = data.files.map((file) =>
                photosService.upload({
                    reservation_id: data.reservation_id,
                    type: data.type,
                    file,
                })
            );

            const uploadedPhotos = await Promise.all(uploadPromises);
            setPhotos([...uploadedPhotos, ...photos]);

            showSuccess('Success!', `${uploadedPhotos.length} photo(s) uploaded successfully`);
            setShowUploadModal(false);
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Failed to upload photos');
        }
    };

    const handleDelete = async (publicId: string) => {
        const confirmed = await showConfirm(
            'Delete Photo?',
            'Are you sure you want to delete this photo? This action cannot be undone.'
        );
        if (!confirmed) return;

        try {
            await photosService.delete(publicId);
            setPhotos(photos.filter((p) => p.public_id !== publicId));
            showSuccess('Deleted!', 'Photo has been deleted successfully.');
        } catch (err: any) {
            showError('Error!', err.response?.data?.message || 'Failed to delete photo');
        }
    };

    const handleViewReservation = (reservation: Reservation) => {
        setSelectedReservation(reservation);
        setShowCarousel(true);
    };

    const handleUploadFromCarousel = () => {
        if (selectedReservation) {
            setPreSelectedReservationId(selectedReservation.public_id);
            setShowCarousel(false);
            setShowUploadModal(true);
        }
    };

    // Group photos by reservation and filter
    const filteredReservationsWithPhotos = useMemo(() => {
        // Filter reservations based on search and house filter
        return reservations
            .filter((reservation) => {
                const house = houses.find((h) => h.public_id === reservation.house_id);

                // Search filter
                const matchesSearch =
                    searchQuery === '' ||
                    reservation.name_first_guess?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    house?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    house?.address?.toLowerCase().includes(searchQuery.toLowerCase());

                // House filter
                const matchesHouse = houseFilter === 'all' || reservation.house_id === houseFilter;

                return matchesSearch && matchesHouse;
            })
            .map((reservation) => {
                const reservationPhotos = photos.filter((p) => p.reservation_id === reservation.public_id);
                const checkInPhotos = reservationPhotos.filter((p) => p.type === 'check-in');
                const checkOutPhotos = reservationPhotos.filter((p) => p.type === 'check-out');

                return {
                    reservation,
                    checkInPhotos,
                    checkOutPhotos,
                    totalPhotos: reservationPhotos.length,
                };
            })
            .sort((a, b) => b.totalPhotos - a.totalPhotos); // Sort by total photos descending
    }, [reservations, photos, houses, searchQuery, houseFilter]);

    const getHouse = (houseId: string) => {
        return houses.find((h) => h.public_id === houseId);
    };

    const getReservationPhotos = (reservationId: string, type: 'check-in' | 'check-out') => {
        return photos.filter((p) => p.reservation_id === reservationId && p.type === type);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading photos...</p>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            {/* Header */}
            <div className={`d-flex ${isMobile ? 'flex-column gap-3' : 'justify-content-between align-items-center'} mb-4`}>
                {!isMobile && (
                    <div>
                        <h4 className="mb-1" style={{ fontSize: '1.5rem' }}>Photos Management</h4>
                        <p className="text-muted small mb-0">
                            {filteredReservationsWithPhotos.length} reservations • {photos.length} total photos
                        </p>
                    </div>
                )}
                {isMobile && (
                    <div className="text-muted small mb-0">
                        {filteredReservationsWithPhotos.length} reservations • {photos.length} total photos
                    </div>
                )}
                {canDelete && (
                    <Button variant="primary" onClick={() => setShowUploadModal(true)} className={isMobile ? 'w-100' : ''}>
                        <Upload size={18} className="me-2" />
                        {isMobile ? 'Upload' : 'Upload Photos'}
                    </Button>
                )}
            </div>

            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        {/* Search */}
                        <Col md={4}>
                            <Form.Label className="small text-muted mb-1">Search</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <Search size={16} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Search by file name, guest, or house..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </InputGroup>
                        </Col>

                        {/* House Filter */}
                        <Col md={8}>
                            <Form.Label className="small text-muted mb-1">Filter by House</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <Filter size={16} />
                                </InputGroup.Text>
                                <Form.Select
                                    value={houseFilter}
                                    onChange={(e) => setHouseFilter(e.target.value)}
                                >
                                    <option value="all">All Houses</option>
                                    {houses.map((house) => (
                                        <option key={house.public_id} value={house.public_id}>
                                            {house.name || house.address}
                                        </option>
                                    ))}
                                </Form.Select>
                            </InputGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Reservations Grid */}
            {filteredReservationsWithPhotos.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <div style={{ fontSize: '4rem' }} className="mb-3">
                            📷
                        </div>
                        <p className="text-muted">
                            {reservations.length === 0
                                ? 'No reservations found'
                                : 'No reservations match your search'}
                        </p>
                        {canDelete && reservations.length > 0 && (
                            <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                                <Upload size={18} className="me-2" />
                                Upload Photos
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            ) : (
                <AnimatePresence mode="wait">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Row className="g-4">
                            {filteredReservationsWithPhotos.map((item, index) => (
                                <Col key={item.reservation.public_id} xs={12} sm={6} md={4} lg={3}>
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                    >
                                        <ReservationPhotoCard
                                            reservation={item.reservation}
                                            house={getHouse(item.reservation.house_id)}
                                            checkInCount={item.checkInPhotos.length}
                                            checkOutCount={item.checkOutPhotos.length}
                                            onClick={() => handleViewReservation(item.reservation)}
                                        />
                                    </motion.div>
                                </Col>
                            ))}
                        </Row>
                    </motion.div>
                </AnimatePresence>
            )}

            {/* Upload Modal */}
            <PhotoUploadModal
                show={showUploadModal}
                onHide={() => {
                    setShowUploadModal(false);
                    setPreSelectedReservationId(undefined);
                }}
                reservations={reservations}
                houses={houses}
                onUpload={handleUpload}
                preSelectedReservationId={preSelectedReservationId}
            />

            {/* Carousel Viewer */}
            <ReservationPhotoCarousel
                show={showCarousel}
                onHide={() => setShowCarousel(false)}
                reservation={selectedReservation}
                house={selectedReservation ? getHouse(selectedReservation.house_id) || null : null}
                checkInPhotos={selectedReservation ? getReservationPhotos(selectedReservation.public_id, 'check-in') : []}
                checkOutPhotos={selectedReservation ? getReservationPhotos(selectedReservation.public_id, 'check-out') : []}
                onDelete={canDelete ? handleDelete : undefined}
                showUploadButton={isSuperAdmin}
                onUploadClick={handleUploadFromCarousel}
            />
        </div>
    );
};

export default Photos;